import { SkeletonLoader } from '@latitude/skeleton-loader';
import { cn } from '@latitude/token-utils';
import type { ReactNode } from 'react';

export type SkeletonCardListItemProps = {
  /** Show the leading avatar/icon placeholder (default: true) */
  showLeading?: boolean;
  /** Width class for the title placeholder (default: 'w-32') */
  titleWidth?: string;
  /** Width class for the subtitle placeholder (default: 'w-48') */
  subtitleWidth?: string;
  /** Custom trailing slot content; defaults to a single square placeholder */
  trailing?: ReactNode;
  className?: string;
  'data-testid'?: string;
};

const defaultTrailing = <SkeletonLoader className="size-6" />;

/**
 * Loading placeholder matching CardListItem's leading/title-subtitle/trailing
 * layout (base + "big" size classes from cardListItemVariants, minus the
 * interactive cursor/hover state — this row isn't clickable).
 */
export const SkeletonCardListItem = ({
  showLeading = true,
  titleWidth = 'w-32',
  subtitleWidth = 'w-48',
  trailing = defaultTrailing,
  className,
  'data-testid': testId = 'skeleton-card-list-item',
}: SkeletonCardListItemProps) => (
  <div
    className={cn(
      'flex w-full items-center',
      'gap-spacing-comp-card-list-big-gap',
      'rounded-radius-comp-card-list',
      'bg-color-comp-card-list-container-enabled',
      'py-spacing-comp-card-list-big-vertical',
      'px-spacing-comp-card-list-big-horizontal',
      className,
    )}
    data-testid={testId}
  >
    {showLeading && (
      <div className="flex min-w-[24px] items-start">
        <SkeletonLoader className="size-10 shrink-0 rounded-full" />
      </div>
    )}
    <div className="flex min-w-0 flex-1 flex-col justify-center gap-1">
      <SkeletonLoader className={cn('h-6', titleWidth)} />
      <SkeletonLoader className={cn('h-4', subtitleWidth)} />
    </div>
    <div className="ml-4 flex shrink-0 items-center">{trailing}</div>
  </div>
);
